// ---------------------------------------------------------------------------
// functions.ts -- Bounded Functions client (the imperative escape hatch).
//
// `bounded.functions.invoke(name, args, opts?)` POSTs to the dispatcher's
// /invoke endpoint, attaching the caller's session token AUTOMATICALLY (the
// exact same token the SDK uses for data reads/writes). Works client + server.
// The dispatcher verifies the token, evaluates the function's `auth` policy
// rule, and dispatches to the function — returning its JSON, or throwing on
// 403 / error.
// ---------------------------------------------------------------------------
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { getConfig } from './config';
import { createAuthHeader } from '../utils/utils';
/** Prod functions dispatcher; overridable via init({ functionsUrl }) or network preset. */
const DEFAULT_FUNCTIONS_URL = 'https://functions.bounded.sh';
export class FunctionInvokeError extends Error {
    constructor(message, statusCode, details) {
        super(message);
        this.statusCode = statusCode;
        this.details = details;
        this.name = 'FunctionInvokeError';
    }
}
function stripAuthHeaders(headers) {
    if (!headers)
        return undefined;
    const { Authorization, authorization } = headers, rest = __rest(headers, ["Authorization", "authorization"]);
    return Object.keys(rest).length > 0 ? rest : undefined;
}
/**
 * Invoke a deployed Bounded Function by name. Returns the function's JSON.
 *
 *   const res = await bounded.functions.invoke('syncStripe', { customerId });
 *
 * Throws FunctionInvokeError on 401 (not logged in / bad token), 403 (the
 * function's auth policy rule denied this caller), 404 (unknown function),
 * 503 (Workers for Platforms not configured), or any non-2xx / transport error.
 */
export async function invoke(name, args = {}, opts = {}) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!name || typeof name !== 'string') {
        throw new FunctionInvokeError('A function name is required');
    }
    // A server WalletClient passes its immutable config snapshot; the default path
    // (browser + top-level server) reads the mutable process-global init() config.
    const config = (_b = (_a = opts._overrides) === null || _a === void 0 ? void 0 : _a._config) !== null && _b !== void 0 ? _b : await getConfig();
    const base = (config.functionsUrl || DEFAULT_FUNCTIONS_URL).replace(/\/$/, '');
    // Attach the caller's session token automatically (same token as data calls).
    // A wallet-scoped call (server WalletClient.invoke) resolves the token from the
    // wallet's own session; browser calls use the active web session. Top-level
    // server calls fail closed because there is no ambient server signer.
    const authHeader = ((_c = opts._overrides) === null || _c === void 0 ? void 0 : _c._getAuthHeaders)
        ? await opts._overrides._getAuthHeaders()
        : await createAuthHeader(config.isServer);
    const headers = Object.assign(Object.assign({ 'Content-Type': 'application/json', 'X-App-Id': config.appId }, ((_d = stripAuthHeaders(opts.headers)) !== null && _d !== void 0 ? _d : {})), (authHeader !== null && authHeader !== void 0 ? authHeader : {}));
    const controller = new AbortController();
    const timeoutMs = (_e = opts.timeoutMs) !== null && _e !== void 0 ? _e : 60000;
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    // `fetch` resolves as soon as the response HEADERS arrive, not when the body
    // has been received, so the abort timer must stay armed across `res.text()`
    // too. Clearing it in a `finally` that covers both the fetch AND the body read
    // means a dispatcher/network that returns headers then stalls (or drip-feeds)
    // the body is torn down at `timeoutMs` instead of leaving `invoke` pending
    // forever and accumulating open connections (DS3-0432).
    let res;
    let text;
    try {
        res = await fetch(`${base}/invoke`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ appId: config.appId, functionName: name, args }),
            signal: controller.signal,
        });
        text = await res.text();
    }
    catch (err) {
        if ((err === null || err === void 0 ? void 0 : err.name) === 'AbortError') {
            throw new FunctionInvokeError(`Function "${name}" invocation timed out after ${timeoutMs}ms`);
        }
        throw new FunctionInvokeError(`Failed to reach the functions dispatcher: ${(_f = err === null || err === void 0 ? void 0 : err.message) !== null && _f !== void 0 ? _f : String(err)}`);
    }
    finally {
        clearTimeout(timer);
    }
    let body = null;
    if (text) {
        try {
            body = JSON.parse(text);
        }
        catch (_j) {
            body = { raw: text };
        }
    }
    if (!res.ok) {
        const message = (_h = (_g = body === null || body === void 0 ? void 0 : body.error) !== null && _g !== void 0 ? _g : body === null || body === void 0 ? void 0 : body.message) !== null && _h !== void 0 ? _h : `Function "${name}" failed with HTTP ${res.status}`;
        throw new FunctionInvokeError(message, res.status, body);
    }
    // The dispatcher returns the function's JSON directly.
    return body;
}
/** The `bounded.functions` namespace surface. */
export const functions = { invoke };
